import { Button } from '@/components/ui/button';
import {
  isConnectableCarrierKind,
  type CarrierConnectionSummary,
  type CarrierKind,
} from '@/lib/api/carrier-connections';

import { CARRIER_CATALOGUE } from './carrier-catalogue';
import { IntegrationCard, type IntegrationCardStatus } from './IntegrationCard';

/**
 * One tile in Step 4's card grid. Carriers we ship today (only
 * `royalmail_cd` at MVP) get a Connect / Manage CTA; the rest render
 * as muted "Coming soon" tiles so the multi-carrier promise reads
 * from day 1 per ADR 0015.
 *
 * Wraps the shared `<IntegrationCard>` chrome (per ADR 0016) with
 * the carrier-domain CTA logic — Connect button when idle, "Connected
 * as …" + Disconnect when active. Token-paste itself lives in
 * `<RoyalMailCdConnectDialog>` and is invoked by Step 4's grid, not
 * here.
 */
interface CarrierCardProps {
  readonly kind: CarrierKind;
  readonly activeConnection?: CarrierConnectionSummary;
  readonly onConnect: () => void;
  readonly onDisconnect: (carrierConnectionId: string) => void;
  readonly disconnecting?: boolean;
}

export function CarrierCard({
  kind,
  activeConnection,
  onConnect,
  onDisconnect,
  disconnecting,
}: CarrierCardProps) {
  const entry = CARRIER_CATALOGUE[kind];
  const connectable = isConnectableCarrierKind(kind);
  const isConnected = activeConnection !== undefined;
  const status: IntegrationCardStatus = isConnected
    ? 'active'
    : connectable
      ? 'connectable'
      : 'coming-soon';

  return (
    <IntegrationCard
      testId={`carrier-card-${kind}`}
      logo={
        <img
          src={entry.logoUrl}
          alt={`${entry.displayName} logo`}
          className="h-10 w-auto max-w-[160px] object-contain"
        />
      }
      displayName={entry.displayName}
      tagline={entry.tagline}
      status={status}
    >
      {isConnected ? (
        <div className="flex items-center justify-between gap-2">
          <span className="text-muted-foreground truncate text-xs">
            Connected as{' '}
            <span className="text-foreground font-medium">{activeConnection.display_name}</span>
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={disconnecting}
            onClick={() => {
              onDisconnect(activeConnection.carrier_connection_id);
            }}
          >
            {disconnecting ? 'Disconnecting…' : 'Disconnect'}
          </Button>
        </div>
      ) : (
        <Button
          type="button"
          size="sm"
          disabled={!connectable}
          onClick={onConnect}
          aria-label={`Connect ${entry.displayName}`}
          className="w-full"
        >
          Connect
        </Button>
      )}
    </IntegrationCard>
  );
}
